import { MediaTag } from "@prisma/client";
import { OnboardingStepType } from "@yardzen/api-dtos";

export interface OnboardingStepUIDetails {
  displayName: string;
  iconName: string;
  stage: number;
  description: string;
  imageSrc: string;
  startDialog: {
    title: string;
    description: string;
    icon: string;
    path: string;
    primaryCta: string;
  };
  completeDialog?: {
    title: string;
    description: string;
    icon: string;
    primaryCta: string;
    path?: string;
  };
}

// Define a type that can be either an OnboardingStepType or one of the additional step IDs
export type StepId =
  | OnboardingStepType
  | "review-design"
  | "feedback"
  | "revisions"
  | "materials"
  | "contractor"
  | "installation";

export const ONBOARDING_STEP_STAGE_MAP: Record<number, string> = {
  1: "Plan Your Space",
  2: "Shape your Design",
  3: "From Vision to Reality",
};

export const ONBOARDING_STEP_UI_DETAILS: Record<
  StepId,
  OnboardingStepUIDetails
> = {
  // Stage 1 Steps
  [OnboardingStepType.GOALS]: {
    displayName: "My Goals",
    iconName: "flag",
    stage: 1,
    description:
      "Tell us about your dream outdoor space. What are your main goals for this project? How do you envision using your new yard?",
    imageSrc: "https://images.yardzen.co/onboarding/dashboard/goals.jpg",
    startDialog: {
      title: "Let's Talk Goals",
      description:
        "Share what you hope to achieve with your yard—whether it's a space to relax, play, grow food, or entertain. Your goals help guide every design decision.",
      icon: "challenge-yard",
      path: "/design-project/:designProjectId/onboarding/onboarding-goals",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "We now have a better understanding of your goals. Our team will start working on a personalized proposal just for you. If you have any more ideas or anything else you'd like to add, feel free to reach out.",
      icon: "challenge-yard",
      primaryCta: "Next: Style & Inspiration →",
      path: "/design-project/:designProjectId/onboarding?step=inspiration",
    },
  },
  [OnboardingStepType.INSPIRATION]: {
    displayName: "My Inspiration",
    iconName: "lightbulb",
    stage: 1,
    description:
      "Share your style preferences and inspiration. What looks inspire you? What elements catch your eye in outdoor spaces?",
    imageSrc: "https://images.yardzen.co/onboarding/dashboard/inspiration.png",
    startDialog: {
      title: "Let's Talk Style",
      description:
        "Tell us what you love. Your style preferences help us create a yard that feels like you—every plant, path, and patio included.",
      icon: "plan",
      path: "/design-project/:designProjectId/onboarding/onboarding-inspiration",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "We now have a better understanding of your style and preferences, and our team will start working on a personalized proposal just for you.\n\nIf you have any more ideas or anything else you'd like to add, feel free to reach out.",
      icon: "plan",
      primaryCta: "Next: My plants →",
      path: "/design-project/:designProjectId/onboarding?step=plants",
    },
  },
  [OnboardingStepType.PLANTS]: {
    displayName: "My Plants",
    iconName: "seedling",
    stage: 1,
    description:
      "No matter your vibe, every thriving garden starts with intention. We want to know: What matters most to you when it comes to your plants?",
    imageSrc: "https://images.yardzen.co/onboarding/dashboard/plants.jpg",
    startDialog: {
      title: "Let's talk plants",
      description:
        "One last quiz! Again, please stick with it all the way to the end!",
      icon: "planting",
      path: "/design-project/:designProjectId/onboarding/onboarding-plants",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "We now have a better understanding of your plant preferences. Our team will start working on a personalized proposal just for you. If you have any more ideas or anything else you'd like to add, feel free to reach out.",
      icon: "party-horn",
      primaryCta: "Next: Wishlist & Budget →",
      path: "/design-project/:designProjectId/onboarding?step=wishlist",
    },
  },
  [OnboardingStepType.WISHLIST]: {
    displayName: "Wishlist & Budget",
    iconName: "heart",
    stage: 1,
    description:
      "List the key elements you want—materials, furniture, or features—then estimate costs to align with your budget.",
    imageSrc: "https://images.yardzen.co/onboarding/dashboard/wishlist.jpg",
    startDialog: {
      title: "Let's Talk Must-Haves",
      description:
        "Tell us what features you'd love to include and how they fit within your budget. We'll use this to guide your design and set realistic expectations.",
      icon: "one-round-of-revisions",
      path: "/design-project/:designProjectId/onboarding/wishlist",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "We now have a better understanding of your wishlist and budget. Our team will start working on a personalized proposal just for you. If you have any more ideas or anything else you'd like to add, feel free to reach out.",
      icon: "party-horn",
      primaryCta: "Next: Photos & Drawings →",
      path: "/design-project/:designProjectId/onboarding?step=photos",
    },
  },
  [OnboardingStepType.PHOTOS]: {
    displayName: "Photos & Drawings",
    iconName: "image",
    stage: 1,
    description:
      "Help us understand your space better. Upload photos of your yard and any existing plans or drawings you have.",
    imageSrc: "https://images.yardzen.co/onboarding/dashboard/photos.jpg",
    startDialog: {
      title: "Let's Talk Visuals",
      description:
        "Let me know the tone you're going for — chill and friendly, premium and polished, or more quirky/personal.",
      icon: "sparkles",
      path: "/design-project/:designProjectId/onboarding/photos?step=inspiration",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "We now have a better understanding of your space. Our team will start working on a personalized proposal just for you. If you have any more ideas or anything else you'd like to add, feel free to reach out.",
      icon: "party-horn",
      primaryCta: "Next: Review & Submit →",
      path: "/design-project/:designProjectId/onboarding?step=review",
    },
  },
  [OnboardingStepType.REVIEW]: {
    displayName: "Review & Submit to Designer",
    iconName: "clipboard-list",
    stage: 1,
    description:
      "Review all your inputs and preferences before submitting to your design team. Make sure everything reflects your vision.",
    imageSrc: "",
    startDialog: {
      title: "Review & Submit",
      description: "Review your information and submit to your design team.",
      icon: "check",
      path: "/design-project/:designProjectId/onboarding/review",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "You've completed onboarding! Our team will start working on your design. If you have any more ideas or anything else you'd like to add, feel free to reach out.",
      icon: "party-horn",
      primaryCta: "Go to Dashboard →",
      path: "/design-project/:designProjectId/dashboard",
    },
  },
  [OnboardingStepType.EXTERIOR]: {
    displayName: "Exterior Details",
    iconName: "house",
    stage: 1,
    description:
      "Choose the colors, finishes, and outdoor extras that reflect your style. These design details will help shape a cohesive, beautiful look for your home's exterior.",
    imageSrc: "https://images.yardzen.co/onboarding/dashboard/exterior.png",
    startDialog: {
      title: "Let's discover your exterior design goals",
      description:
        "Please spend 5-10 mins completing this quiz to give your designers a broader perspective of what you like and need for your exterior design.",
      icon: "exterior-home",
      path: "/design-project/:designProjectId/onboarding/onboarding-exterior",
      primaryCta: "Start →",
    },
    completeDialog: {
      title: "Success!",
      description:
        "We now have a better understanding of your exterior details. Our team will create a tailored exterior design proposal for you. If you have more ideas to share, don’t hesitate to contact us.",
      icon: "party-horn",
      primaryCta: "Go to Dashboard →",
      path: "/design-project/:designProjectId/dashboard",
    },
  },

  // Stage 2 Steps
  "review-design": {
    displayName: "Review Design",
    iconName: "blueprint",
    stage: 2,
    description:
      "Take a look at your initial design and get ready to give feedback.",
    imageSrc: "/images/review-design-sample.jpg",
    startDialog: {
      title: "Review Your Design",
      description:
        "Take a look at your initial design and get ready to give feedback.",
      icon: "blueprint",
      path: "/design-project/:designProjectId/review-design",
      primaryCta: "Start →",
    },
  },
  feedback: {
    displayName: "Provide Feedback",
    iconName: "conversation",
    stage: 2,
    description: "Share your thoughts and suggestions to refine your design.",
    imageSrc: "/images/feedback-sample.jpg",
    startDialog: {
      title: "Give Feedback",
      description: "Share your thoughts and suggestions to refine your design.",
      icon: "conversation",
      path: "/design-project/:designProjectId/feedback",
      primaryCta: "Start →",
    },
  },
  revisions: {
    displayName: "Revisions",
    iconName: "pencil",
    stage: 2,
    description: "Request changes and see your design evolve.",
    imageSrc: "/images/revisions-sample.jpg",
    startDialog: {
      title: "Request Revisions",
      description: "Request changes and see your design evolve.",
      icon: "pencil",
      path: "/design-project/:designProjectId/revisions",
      primaryCta: "Start →",
    },
  },

  // Stage 3 Steps
  materials: {
    displayName: "Materials Selection",
    iconName: "pallet",
    stage: 3,
    description: "Choose the materials and finishes for your project.",
    imageSrc: "/images/materials-sample.jpg",
    startDialog: {
      title: "Select Materials",
      description: "Choose the materials and finishes for your project.",
      icon: "pallet",
      path: "/design-project/:designProjectId/materials",
      primaryCta: "Start →",
    },
  },
  contractor: {
    displayName: "Find a Contractor",
    iconName: "handshake",
    stage: 3,
    description:
      "Get matched with trusted contractors to bring your vision to life.",
    imageSrc: "/images/contractor-sample.jpg",
    startDialog: {
      title: "Find a Contractor",
      description:
        "Get matched with trusted contractors to bring your vision to life.",
      icon: "handshake",
      path: "/design-project/:designProjectId/contractor",
      primaryCta: "Start →",
    },
  },
  installation: {
    displayName: "Installation Support",
    iconName: "helmet",
    stage: 3,
    description:
      "Access support and resources for a smooth installation process.",
    imageSrc: "/images/installation-sample.jpg",
    startDialog: {
      title: "Installation Support",
      description:
        "Access support and resources for a smooth installation process.",
      icon: "helmet",
      path: "/design-project/:designProjectId/installation",
      primaryCta: "Start →",
    },
  },
};

export enum YardType {
  FRONT_YARD = "front-yard",
  BACK_YARD = "backyard",
  LEFT_SIDE_YARD = "left-side-yard",
  RIGHT_SIDE_YARD = "right-side-yard",
}

export enum PhotoByYardTypeStep {
  HORIZONTAL_PHOTOS = "horizontal-photos",
  SLOW_PAN_VIDEO = "slow-pan-video",
  CHATTY_VIDEO = "chatty-video",
  SLOPE = "slope",
  KEEP_REMOVE = "keep-remove",
}

export enum GeneralPhotoStep {
  INSPIRATION = "inspiration",
  YARD_TRACKING = "yard-tracking",
  PLANS_AND_DRAWINGS = "plans-and-drawings",
}

type YardTypeStepMapping = {
  [key in YardType]: PhotoByYardTypeStep[];
};

type PhotoStepDetails = {
  displayName: string;
  badgeText: string;
  badgeVariant: "warning" | "default";
  stepOrder: number;
  description: string;
  imageSrc: string;
  acceptedFileTypes?: Record<string, string[]>;
};

export const PHOTO_FILE_TYPES = {
  "image/jpeg": [],
  "image/png": [],
  "image/svg+xml": [],
  "image/heic": [],
  "image/webp": [],
};

const VIDEO_FILE_TYPES = {
  "video/mp4": [],
  "video/quicktime": [],
  "video/mov": [],
};

export const PHOTO_BY_YARD_TYPE_STEPS: Record<
  PhotoByYardTypeStep,
  PhotoStepDetails
> = {
  [PhotoByYardTypeStep.HORIZONTAL_PHOTOS]: {
    displayName: "Horizontal Photos",
    badgeText: "At least 6 images required",
    badgeVariant: "warning",
    stepOrder: 1,
    description:
      "Take a photo at each of the arrows in the specified direction. Make sure to have your phone in landscape (horizontal) mode at 1x (no zoom or panoramic). These photos need to include the entirety of your yard and house so we can model it properly.",
    imageSrc:
      "https://images.yardzen.co/onboarding/photoExamples/horizontal.jpg",
    acceptedFileTypes: PHOTO_FILE_TYPES,
  },
  [PhotoByYardTypeStep.SLOW_PAN_VIDEO]: {
    displayName: "Slow-pan Video",
    badgeText: "At least 1 video required",
    badgeVariant: "warning",
    stepOrder: 2,
    description:
      "Slowly pan around each area of your yard and make sure to capture everything (including your house)!",
    imageSrc:
      "https://images.yardzen.co/onboarding/photoExamples/slowpan-video.png",
    acceptedFileTypes: VIDEO_FILE_TYPES,
  },
  [PhotoByYardTypeStep.CHATTY_VIDEO]: {
    displayName: "Chatty Video",
    badgeText: "Optional video",
    badgeVariant: "default",
    stepOrder: 3,
    description:
      "If there are areas where you need to highlight special circumstances, let us know in a 2-3 min long video.",
    imageSrc:
      "https://images.yardzen.co/onboarding/photoExamples/chatty-video.png",
    acceptedFileTypes: VIDEO_FILE_TYPES,
  },
  [PhotoByYardTypeStep.SLOPE]: {
    displayName: "Slope",
    badgeText: "Optional for sloped areas",
    badgeVariant: "default",
    stepOrder: 4,
    description:
      "Take photos from the perimeter of the property facing the side of the slope, so  we're able to determine the angle and length of the slope. Make sure to have your phone in landscape (horizontal) mode at 1x (no zoom or panoramic).",
    imageSrc: "https://images.yardzen.co/onboarding/photoExamples/slope.png",
    acceptedFileTypes: PHOTO_FILE_TYPES,
  },
  [PhotoByYardTypeStep.KEEP_REMOVE]: {
    displayName: "Keep/Remove",
    badgeText: "Optional",
    badgeVariant: "default",
    stepOrder: 5,
    description:
      "Show us what you'd like to keep and what you'd like to remove. Please take a clear shot of the item and leave a short comment for each image. This is very helpful for your designer.",
    imageSrc:
      "https://images.yardzen.co/onboarding/photoExamples/keep-remove.png",
    acceptedFileTypes: PHOTO_FILE_TYPES,
  },
};

export const PHOTO_ONBOARDING_STEPS_BY_YARD_TYPE: YardTypeStepMapping = {
  [YardType.FRONT_YARD]: [
    PhotoByYardTypeStep.HORIZONTAL_PHOTOS,
    PhotoByYardTypeStep.SLOW_PAN_VIDEO,
    PhotoByYardTypeStep.CHATTY_VIDEO,
    PhotoByYardTypeStep.SLOPE,
    PhotoByYardTypeStep.KEEP_REMOVE,
  ],
  [YardType.BACK_YARD]: [
    PhotoByYardTypeStep.HORIZONTAL_PHOTOS,
    PhotoByYardTypeStep.SLOW_PAN_VIDEO,
    PhotoByYardTypeStep.CHATTY_VIDEO,
    PhotoByYardTypeStep.SLOPE,
    PhotoByYardTypeStep.KEEP_REMOVE,
  ],
  [YardType.LEFT_SIDE_YARD]: [
    PhotoByYardTypeStep.HORIZONTAL_PHOTOS,
    PhotoByYardTypeStep.SLOW_PAN_VIDEO,
    PhotoByYardTypeStep.CHATTY_VIDEO,
    PhotoByYardTypeStep.SLOPE,
    PhotoByYardTypeStep.KEEP_REMOVE,
  ],
  [YardType.RIGHT_SIDE_YARD]: [
    PhotoByYardTypeStep.HORIZONTAL_PHOTOS,
    PhotoByYardTypeStep.SLOW_PAN_VIDEO,
    PhotoByYardTypeStep.CHATTY_VIDEO,
    PhotoByYardTypeStep.SLOPE,
    PhotoByYardTypeStep.KEEP_REMOVE,
  ],
};

export const PHOTO_STEP_MEDIA_TAGS: {
  [yardType in YardType]: {
    [step in PhotoByYardTypeStep]?: MediaTag[];
  };
} = {
  [YardType.FRONT_YARD]: {
    [PhotoByYardTypeStep.HORIZONTAL_PHOTOS]: [MediaTag.FRONT_YARD_HORIZONTAL],
    [PhotoByYardTypeStep.KEEP_REMOVE]: [
      MediaTag.FRONT_YARD_KEEP,
      MediaTag.FRONT_YARD_REMOVE,
    ],
    [PhotoByYardTypeStep.SLOPE]: [MediaTag.FRONT_YARD_SLOPE],
    [PhotoByYardTypeStep.CHATTY_VIDEO]: [MediaTag.FRONT_YARD_CHATTY_VIDEO],
    [PhotoByYardTypeStep.SLOW_PAN_VIDEO]: [MediaTag.FRONT_YARD_SLOW_PAN_VIDEO],
  },
  [YardType.BACK_YARD]: {
    [PhotoByYardTypeStep.HORIZONTAL_PHOTOS]: [MediaTag.BACK_YARD_HORIZONTAL],
    [PhotoByYardTypeStep.KEEP_REMOVE]: [
      MediaTag.BACK_YARD_KEEP,
      MediaTag.BACK_YARD_REMOVE,
    ],
    [PhotoByYardTypeStep.SLOPE]: [MediaTag.BACK_YARD_SLOPE],
    [PhotoByYardTypeStep.CHATTY_VIDEO]: [MediaTag.BACK_YARD_CHATTY_VIDEO],
    [PhotoByYardTypeStep.SLOW_PAN_VIDEO]: [MediaTag.BACK_YARD_SLOW_PAN_VIDEO],
  },
  [YardType.LEFT_SIDE_YARD]: {
    [PhotoByYardTypeStep.HORIZONTAL_PHOTOS]: [
      MediaTag.LEFT_SIDE_YARD_HORIZONTAL,
    ],
    [PhotoByYardTypeStep.KEEP_REMOVE]: [
      MediaTag.LEFT_SIDE_YARD_KEEP,
      MediaTag.LEFT_SIDE_YARD_REMOVE,
    ],
    [PhotoByYardTypeStep.SLOPE]: [MediaTag.LEFT_SIDE_YARD_SLOPE],
    [PhotoByYardTypeStep.CHATTY_VIDEO]: [MediaTag.LEFT_SIDE_YARD_CHATTY_VIDEO],
    [PhotoByYardTypeStep.SLOW_PAN_VIDEO]: [
      MediaTag.LEFT_SIDE_YARD_SLOW_PAN_VIDEO,
    ],
  },
  [YardType.RIGHT_SIDE_YARD]: {
    [PhotoByYardTypeStep.HORIZONTAL_PHOTOS]: [
      MediaTag.RIGHT_SIDE_YARD_HORIZONTAL,
    ],
    [PhotoByYardTypeStep.KEEP_REMOVE]: [
      MediaTag.RIGHT_SIDE_YARD_KEEP,
      MediaTag.RIGHT_SIDE_YARD_REMOVE,
    ],
    [PhotoByYardTypeStep.SLOPE]: [MediaTag.RIGHT_SIDE_YARD_SLOPE],
    [PhotoByYardTypeStep.CHATTY_VIDEO]: [MediaTag.RIGHT_SIDE_YARD_CHATTY_VIDEO],
    [PhotoByYardTypeStep.SLOW_PAN_VIDEO]: [
      MediaTag.RIGHT_SIDE_YARD_SLOW_PAN_VIDEO,
    ],
  },
};

export const YARD_TYPE_DISPLAY_NAME: Record<YardType, string> = {
  [YardType.FRONT_YARD]: "Front Yard",
  [YardType.BACK_YARD]: "Backyard",
  [YardType.LEFT_SIDE_YARD]: "Left Side Yard",
  [YardType.RIGHT_SIDE_YARD]: "Right Side Yard",
};
